class CartShippingProtection extends HTMLElement {
  constructor() {
    super();

    this.priceEl = this.querySelector(".js-protection-price");
    this.input = this.querySelector(".js-protection-input");

    this.protectionProduct = JSON.parse(
      this.querySelector("[data-protection-json]").textContent
    );

    // Serialized queue of validation runs so cart removals/adds never race
    this.pending = Promise.resolve();
    this.inFlight = 0;

    this.onCheckoutSubmit = this.onCheckoutSubmit.bind(this);
    this.onCartEvent = (event) => this.validateShippingProtection(event);
    this.onInputChange = this.onInputChange.bind(this);
  }

  connectedCallback() {
    this.defaultStatus = this.dataset.defaultStatus;

    this.validateShippingProtection(this.input.checked);

    this.input.addEventListener("input", this.onInputChange);
    document.addEventListener("submit", this.onCheckoutSubmit, true);
    document.addEventListener("theme:product:add", this.onCartEvent);
    document.addEventListener("theme:cart:change", this.onCartEvent);
  }

  // The drawer can re-render this element; drop document listeners so stale
  // instances don't keep validating or intercepting checkout
  disconnectedCallback() {
    this.input.removeEventListener("input", this.onInputChange);
    document.removeEventListener("submit", this.onCheckoutSubmit, true);
    document.removeEventListener("theme:product:add", this.onCartEvent);
    document.removeEventListener("theme:cart:change", this.onCartEvent);
  }

  formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    let value = "";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || "${{amount}}";

    function formatWithDelimiters(
      number,
      precision = 2,
      thousands = ",",
      decimal = "."
    ) {
      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split(".");
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`
      );
      const centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = formatWithDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = formatWithDelimiters(cents, 0, " ", "");
        break;
      case "amount_with_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ",");
        break;
      case "amount_with_period_and_space_separator":
        value = formatWithDelimiters(cents, 2, " ", ".");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  async getCart() {
    return fetch(`${theme.routes.cart_url}.js`, {
      credentials: "same-origin",
      method: "GET",
    }).then((response) => response.json());
  }

  async changeItem(key, qty) {
    return this._updateCart({
      url: ""
        .concat(`${theme.routes.cart_change_url}.js`, "?t=")
        .concat(Date.now()),
      data: JSON.stringify({
        id: key,
        quantity: qty,
      }),
    });
  }

  async refreshCart() {
    document.querySelector("cart-items").getCart();
  }

  async _updateCart(params) {
    return fetch(params.url, {
      method: "POST",
      body: params.data,
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => response.json())
      .then(function (cart) {
        return cart;
      });
  }

  getStoredPreference() {
    try {
      return localStorage.getItem("shippingProtection");
    } catch (e) {
      return null;
    }
  }

  setStoredPreference(value) {
    try {
      localStorage.setItem("shippingProtection", value);
    } catch (e) {
      // Storage unavailable (private mode, blocked cookies) - preference just won't persist
    }
  }

  // Tier whose [cartMin, cartMax) range contains the subtotal. If the subtotal
  // falls in a gap between tiers (or outside all of them), use the nearest tier;
  // ties go to the higher tier so coverage is never under-priced.
  findProtectionVariant(subtotal) {
    const variants = this.protectionProduct.variants || [];

    const exact = variants.find(
      (variant) => variant.cartMin <= subtotal && variant.cartMax > subtotal
    );
    if (exact) return exact;

    const distanceTo = (variant) => {
      if (subtotal < variant.cartMin) return variant.cartMin - subtotal;
      return subtotal - variant.cartMax + 1;
    };

    return variants.reduce((closest, variant) => {
      if (!closest) return variant;
      const diff = distanceTo(variant) - distanceTo(closest);
      if (diff < 0) return variant;
      if (diff === 0 && variant.cartMin > closest.cartMin) return variant;
      return closest;
    }, null);
  }

  validateShippingProtection(addProtection = true) {
    this.inFlight += 1;
    this.pending = this.pending
      .then(() => this._validateShippingProtection(addProtection))
      .catch((error) => console.error("[shipping-protection]", error))
      .finally(() => {
        this.inFlight -= 1;
      });

    return this.pending;
  }

  async _validateShippingProtection(addProtection) {
    const cart = await this.getCart();

    const shippableItems = cart.items
      .filter((item) => !item.properties?._is_shipping_protection)
      .filter((item) => !item.properties?._is_gift_wrap)
      .filter((item) => item.requires_shipping);

    const subtotal = shippableItems
      .map((item) => item.final_line_price)
      .reduce((a, b) => a + b, 0);

    const protectionLines = cart.items.filter(
      (item) => !!item.properties?._is_shipping_protection
    );

    const requiredProtection =
      shippableItems.length > 0 ? this.findProtectionVariant(subtotal) : null;

    const storedPreference = this.getStoredPreference();
    const isCartEvent = typeof addProtection?.type !== "undefined";

    let wantsProtection = !!addProtection;
    if (storedPreference === "false") {
      wantsProtection = false;
    } else if (
      isCartEvent &&
      this.defaultStatus === "ignore" &&
      !storedPreference
    ) {
      // Shopper never opted in: keep whatever state the cart already has
      wantsProtection = protectionLines.length > 0;
    }

    const shouldProtect = wantsProtection && !!requiredProtection;

    const lineToKeep = shouldProtect
      ? protectionLines.find(
          (line) => line.variant_id === requiredProtection.id && line.quantity === 1
        )
      : null;

    let refreshRequired = false;

    // Only ever remove protection lines - never the shopper's products
    for (const line of protectionLines) {
      if (line === lineToKeep) continue;
      await this.changeItem(line.key, 0);
      refreshRequired = true;
    }

    if (shouldProtect && !lineToKeep) {
      await this._updateCart({
        url: `${theme.routes.cart_add_url}.js`,
        data: JSON.stringify({
          items: [
            {
              id: requiredProtection.id,
              quantity: 1,
              properties: {
                _is_shipping_protection: true,
              },
            },
          ],
        }),
      });
      refreshRequired = true;
    }

    this.classList[shippableItems.length === 0 ? "add" : "remove"]("hidden");
    this.classList[requiredProtection ? "remove" : "add"]("hide");
    this.priceEl.innerHTML = requiredProtection
      ? this.formatMoney(requiredProtection.price, theme.moneyFormat)
      : "";
    this.input.checked = shouldProtect;

    if (refreshRequired) this.refreshCart();
  }

  // If the shopper hits checkout while protection is still syncing, hold the
  // submit until it settles (capped at 3s), then always let checkout proceed.
  // When nothing is syncing the native submit is never touched.
  async onCheckoutSubmit(event) {
    const submitter = event.submitter;
    if (!submitter || submitter.name !== "checkout") return;
    if (!this.isConnected || this.inFlight === 0 || this.checkoutReleased) return;

    event.preventDefault();

    const timeout = new Promise((resolve) => setTimeout(resolve, 3000));
    await Promise.race([this.pending, timeout]);

    this.checkoutReleased = true;
    try {
      event.target.requestSubmit(submitter);
    } catch (e) {
      window.location.href = `${theme.routes.root}checkout`;
    } finally {
      this.checkoutReleased = false;
    }
  }

  onInputChange(e) {
    const checked = e.target.checked;
    this.setStoredPreference(checked);
    this.validateShippingProtection(checked);
  }
}

customElements.define("cart-shipping-protection", CartShippingProtection);